import { useEffect, useState } from "react";
import firebaseService from "../../../firebase_service/firebaseService";

export type DeviceCommandStatus = "PENDING" | "SUCCESS" | "FAILURE";

export type DevicePendingCommand = {
  id: string;
  name: string;
  params?: unknown;
  time?: Date;
  timeout?: number;
  expiresAt?: Date;
  local?: boolean;
};

export type DeviceCommandHistoryEntry = {
  id: string;
  name: string;
  params?: unknown;
  status?: DeviceCommandStatus;
  reason?: string;
  receiveTime?: Date;
  executeTime?: Date;
};

export const HISTORY_PAGE_SIZE = 10;

// Firestore Timestamp | number | null -> Date | undefined.
const toOptionalDate = (value: unknown) =>
  value ? firebaseService.toDate(value) : undefined;

const machineDoc = (deviceId: string) =>
  firebaseService.db.collection("machines").doc(deviceId);

// Live list of all commands the clock has not picked up yet
// (machines/{id}/commands). The clock deletes a command when it receives it,
// so this list is short.
export const useDevicePendingCommands = (deviceId: string) => {
  const [commands, setCommands] = useState<DevicePendingCommand[]>();
  const [error, setError] = useState<Error>();

  useEffect(() => {
    setCommands(undefined);
    setError(undefined);
    return machineDoc(deviceId)
      .collection("commands")
      .onSnapshot(
        (snap) => {
          const list = snap.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.name,
              params: data.params,
              time: toOptionalDate(data.time),
              timeout: data.timeout,
              expiresAt: toOptionalDate(data.expiresAt),
              local: data.local,
            };
          });
          list.sort(
            (a, b) => (b.time?.getTime() ?? 0) - (a.time?.getTime() ?? 0),
          );
          setCommands(list);
        },
        (err) => setError(err),
      );
  }, [deviceId]);

  return { commands, error, isLoading: !commands && !error };
};

// Live list of the latest `limit` executed commands
// (machines/{id}/commandsHistory), newest first.
// If `name` is set, only commands of that name are returned. That case reads
// all history docs of that name and sorts on the client, so no composite
// Firestore index (name + receiveTime) is needed.
export const useDeviceCommandsHistory = (
  deviceId: string,
  limit: number,
  name?: string,
) => {
  const [entries, setEntries] = useState<DeviceCommandHistoryEntry[]>();
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    setError(undefined);
    const ref = machineDoc(deviceId).collection("commandsHistory");
    // Fetch one extra doc to know if there is more to show.
    const query = name
      ? ref.where("name", "==", name)
      : ref.orderBy("receiveTime", "desc").limit(limit + 1);

    return query.onSnapshot(
      (snap) => {
        const list = snap.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name,
            params: data.params,
            status: data.status,
            reason: data.reason,
            receiveTime: toOptionalDate(data.receiveTime),
            executeTime: toOptionalDate(data.executeTime),
          };
        });
        list.sort(
          (a, b) =>
            (b.receiveTime?.getTime() ?? 0) - (a.receiveTime?.getTime() ?? 0),
        );
        setHasMore(list.length > limit);
        setEntries(list.slice(0, limit));
      },
      (err) => setError(err),
    );
  }, [deviceId, limit, name]);

  return { entries, hasMore, error, isLoading: !entries && !error };
};
